export type EntityLinkType =
  | 'client'
  | 'policy'
  | 'quotation'
  | 'insurer'
  | 'partner'
  | 'intermediary'
  | 'receipt';

/** Loose shape — callers pass whatever model they hold (IClient, IPolicy, IPolicyListing, …). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type EntityRef = { id?: number | null } & Record<string, any>;

export interface EntityRoute {
  path: string;
  queryParams?: Record<string, string | number>;
}

// Policy subclass name (backend `GetType().Name`) → detail route segment in policies-routing.module.ts.
// Scheme masters, house-owner and goods-in-transit have no policy detail route, so they stay unlinked.
const POLICY_TYPE_SEGMENTS: Record<string, string> = {
  MotorPolicy: 'motor',
  GeneralPolicy: 'general',
  TravelPolicy: 'travel',
  LifePolicy: 'life',
  CropPolicy: 'crop',
  StatedBenefitPolicy: 'stated-benefit',
};
export const KNOWN_POLICY_ROUTE_SUBTYPES = new Set(Object.values(POLICY_TYPE_SEGMENTS));

const slug = (s: string) => s.trim().toLowerCase().replace(/\s+/g, '-');

function policySegment(ref: EntityRef): string | null {
  if (ref['type']) return POLICY_TYPE_SEGMENTS[ref['type']] ?? null;
  const cob = ref['classOfBusinessName'] ?? ref['product']?.classOfBusiness?.name;
  if (!cob) return null;
  const s = slug(cob);
  return KNOWN_POLICY_ROUTE_SUBTYPES.has(s) ? s : 'general';
}

/** Absolute detail route for a resource, or null when there's no detail page to go to. */
export function entityRoute(type: EntityLinkType, ref: EntityRef | null | undefined): EntityRoute | null {
  const id = ref?.id;
  if (!ref || id == null) return null;

  switch (type) {
    case 'client': {
      const clientType = ref['type'] ?? ref['clientType'];
      return clientType ? { path: `/dashboard/clients/${id}/${String(clientType).toLowerCase()}` } : null;
    }
    case 'policy': {
      const seg = policySegment(ref);
      return seg ? { path: `/dashboard/policies/${seg}/${id}` } : null;
    }
    case 'quotation': {
      // quotation-routing.module.ts `:type/:id` switches on class of business; life/stated-benefit have their own routes.
      const cob = ref['classOfBusinessName'] ?? ref['classOfBusiness']?.name ?? ref['product']?.classOfBusiness?.name;
      return cob ? { path: `/dashboard/quotations/${slug(cob)}/${id}` } : null;
    }
    case 'intermediary': {
      const kind = String(ref['type'] ?? '').toLowerCase();
      if (kind === 'broker') return { path: `/dashboard/intermediaries/brokers/${id}` };
      if (kind !== 'agent') return null;
      // The agent page reads insurerId from the query string (admin users have no insurer of their own).
      const insurerId = ref['insurerId'] ?? ref['insurers']?.[0]?.id;
      return {
        path: `/dashboard/intermediaries/agents/${id}`,
        ...(insurerId ? { queryParams: { insurerId } } : {}),
      };
    }
    case 'insurer':
      return { path: `/dashboard/insurers/${id}` };
    case 'partner':
      // The partner page scopes its tabs by ?insurerId= when the viewer isn't an insurer user.
      return {
        path: `/dashboard/partners/${id}`,
        ...(ref['insurerId'] ? { queryParams: { insurerId: ref['insurerId'] } } : {}),
      };
    case 'receipt':
      return { path: `/dashboard/receipts/${id}` };
  }
}
